import type { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, Link2, Megaphone, Bell, Settings } from 'lucide-react';
import clsx from 'clsx';
import { useUIStore } from '@/shared/stores/ui-store';
import { useNotificationsStore } from '@/shared/stores/notifications-store';
import { useAccountHealthStore } from '@/shared/stores/account-health-store';
import styles from './AppSidebar.module.scss';

interface NavItem {
  label: string;
  icon: ReactNode;
  path: string;
  exact?: boolean;
  badge?: number;
  /** Unread-style dot for a state that needs attention but has no useful count. */
  alert?: boolean;
  alertLabel?: string;
}

export function AppSidebar() {
  const { t } = useTranslation();
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const mobileSidebarOpen = useUIStore((s) => s.mobileSidebarOpen);
  const setMobileSidebarOpen = useUIStore((s) => s.setMobileSidebarOpen);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const accountsNeedingReconnect = useAccountHealthStore((s) => s.reconnectCount);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const navItems: NavItem[] = [
    {
      label: t('nav.dashboard'),
      icon: <LayoutDashboard size={20} />,
      path: '/promote',
      exact: true,
    },
    {
      label: t('nav.accounts'),
      icon: <Link2 size={20} />,
      path: '/promote/accounts',
      // A dot, not a count: the number of broken accounts is rarely more than
      // one and carries no information the page doesn't show better.
      alert: accountsNeedingReconnect > 0,
      alertLabel:
        accountsNeedingReconnect === 1
          ? '1 account needs reconnecting'
          : `${accountsNeedingReconnect} accounts need reconnecting`,
    },
    { label: t('nav.campaigns'), icon: <Megaphone size={20} />, path: '/promote/campaigns' },
    {
      label: t('nav.notifications'),
      icon: <Bell size={20} />,
      path: '/promote/notifications',
      badge: unreadCount,
    },
    { label: t('nav.settings'), icon: <Settings size={20} />, path: '/promote/settings' },
  ];

  const visibleItems = navItems;
  const isItemActive = (item: NavItem) =>
    item.exact
      ? pathname === item.path
      : pathname === item.path || pathname.startsWith(`${item.path}/`);

  return (
    <>
      <aside
        className={clsx(
          styles.sidebar,
          sidebarCollapsed && styles.collapsed,
          mobileSidebarOpen && styles.open,
        )}
      >
        <div className={styles.logo}>
          <span className={styles.logoText}>Promote</span>
        </div>
        <nav className={styles.nav} aria-label="Main navigation">
          <ul className={styles.navList} role="list">
            {visibleItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={clsx(styles.navItem, isItemActive(item) && styles.active)}
                  title={sidebarCollapsed ? item.label : undefined}
                  onClick={() => setMobileSidebarOpen(false)}
                >
                  <span className={styles.navIcon}>{item.icon}</span>
                  {!sidebarCollapsed && <span className={styles.navLabel}>{item.label}</span>}
                  {item.badge != null && item.badge > 0 && (
                    <span className={styles.navBadge} aria-label={`${item.badge} unread`}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                  {item.alert && (
                    <span
                      className={styles.navAlertDot}
                      role="status"
                      aria-label={item.alertLabel}
                    />
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
      {mobileSidebarOpen && (
        <div
          className={styles.overlay}
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
}
